<template>
  <Modal
    ref="modalPeopleUpload"
    :title="$t('PeopleService.Modal.title')"
    width="60%"
    :zIndex="999"
    :has-tab="false"
  >
    <template v-slot:[`no-tab`]>
      <div class="modal-upload">
        <div class="modal-upload-info" v-show="!showForm && !loading">
          <p>
            {{ _headerInstruction }}
          </p>
          <!--
            Templates duais (plano híbrido §4, D-H6): um link por fluxo
            habilitado — legado baixa o modelo da develop (sempre pt);
            "(Novo)" baixa o modelo por locale do branch.
          -->
          <a
            v-if="flags.legacyImportEnabled"
            data-test-template-legacy
            :href="LEGACY_TEMPLATE_URL"
            download
            >{{ $t('PeopleService.Modal.downloadSheets') }}</a
          >
          <a
            v-if="flags.v2ImportEnabled"
            data-test-template-v2
            :href="_v2TemplateUrl"
            download
            >{{ _v2DownloadLabel }}</a
          >
        </div>

        <AddFile
          @file="validateFile"
          :label="fileName"
          :instruction="_fileInstruction"
        />

        <div v-if="showForm">
          <span class="modal-upload-subtitle">
            {{ $t('PeopleService.Modal.subtitle') }}
          </span>
          <v-divider></v-divider>
        </div>

        <div v-if="loading" class="loading">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <v-container fluid v-if="showForm">
          <!--
            Radios por opção { action, flow } (plano híbrido §3.3.1):
            legadas (develop) gated por legacyImportEnabled; "(Novo)" gated
            por v2ImportEnabled — flags de GET /sync/settings.
          -->
          <v-radio-group
            v-model="selectedOptionId"
            inline
            class="modal-upload-action"
          >
            <v-radio
              v-for="option in _uploadOptions"
              :key="option.id"
              :data-test-upload-option="option.id"
              :label="$t(option.labelKey)"
              :value="option.id"
            ></v-radio>
          </v-radio-group>
        </v-container>

        <v-form
          ref="form"
          @submit.prevent
          v-show="showForm"
          class="modal-upload-form"
          v-model="valid"
        >
          <v-container>
            <div class="single-input">
              <!--
                :key derivado do contador de limpeza (fix B3): quando a aba
                re-selecionada é a MESMA, a prop selectedItem não muda e o
                v-autocomplete esvaziado pelo form.reset() não re-sincroniza
                — o re-mount garante o select refletindo selectedTab
              -->
              <SingleSelection
                :key="`tab-select-${tabSelectKey}`"
                :selectedItem="selectedTab"
                :label="$t('PeopleService.Modal.labels.peopleLabel')"
                :placeholder="$t('PeopleService.Modal.labels.peopleLabel')"
                :items="tabs"
                @selectOption="handleTabSelection"
              />
            </div>
            <!--
              Selects do fluxo LEGADO (develop UploadModal): 10 attributes,
              role obrigatório — restaurados sob a opção legada (R1).
            -->
            <div
              v-if="selectedTab && _isLegacyFlow && _isAddUpdateAction"
              :key="`legacy-add-update-${selectedTab}`"
              class="inline-inputs"
            >
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.peopleCodeLabel')"
                  :placeholder="
                    $t('PeopleService.Modal.labels.peopleCodeLabel')
                  "
                  :items="peopleColumns"
                  :selectedItem="_columnByAttribute.code || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'code')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.columnLabelFirstName')"
                  :placeholder="
                    $t('PeopleService.Modal.labels.columnLabelFirstName')
                  "
                  :items="peopleColumns"
                  :rules="[rules.required]"
                  :selectedItem="_columnByAttribute.firstname || ''"
                  @selectOption="handleSelectOption($event, 'firstname')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.columnLabelLastName')"
                  :placeholder="
                    $t('PeopleService.Modal.labels.columnLabelLastName')
                  "
                  :items="peopleColumns"
                  :rules="[rules.required]"
                  :selectedItem="_columnByAttribute.lastname || ''"
                  @selectOption="handleSelectOption($event, 'lastname')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.emailLabel')"
                  :placeholder="$t('PeopleService.Modal.labels.emailLabel')"
                  :items="peopleColumns"
                  :rules="[rules.required]"
                  :selectedItem="_columnByAttribute.email || ''"
                  @selectOption="handleSelectOption($event, 'email')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.attributesLabel')"
                  :placeholder="
                    $t('PeopleService.Modal.labels.attributesLabel')
                  "
                  :items="peopleColumns"
                  :selectedItem="_columnByAttribute.attributes || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'attributes')"
                />
              </div>
              <!--
                'Grupo de Permissionamento' (SINGULAR) — papel sistêmico do
                data-import. Difere por 1 caractere do select de grupos de
                permissionamento do fluxo novo (§5.1) — title distintivo.
              -->
              <div
                class="input-holder"
                :title="$t('PeopleService.Modal.labels.roleHint')"
              >
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.roleLabel')"
                  :placeholder="$t('PeopleService.Modal.labels.roleLabel')"
                  :items="peopleColumns"
                  :rules="[rules.required]"
                  :selectedItem="_columnByAttribute.role || ''"
                  @selectOption="handleSelectOption($event, 'role')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.birthDate')"
                  :placeholder="$t('PeopleService.Modal.labels.birthDate')"
                  :items="peopleColumns"
                  :selectedItem="_columnByAttribute.birthDate || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'birthDate')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.admissionDate')"
                  :placeholder="$t('PeopleService.Modal.labels.admissionDate')"
                  :items="peopleColumns"
                  :selectedItem="_columnByAttribute.admissionDate || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'admissionDate')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.directManager')"
                  :placeholder="$t('PeopleService.Modal.labels.directManager')"
                  :items="peopleColumns"
                  :selectedItem="_columnByAttribute.directManager || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'directManager')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.occupation')"
                  :placeholder="$t('PeopleService.Modal.labels.occupation')"
                  :items="peopleColumns"
                  :selectedItem="_columnByAttribute.occupation || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'occupation')"
                />
              </div>
            </div>
            <!-- Delete LEGADO (develop): apenas o e-mail -->
            <div
              v-if="selectedTab && _isLegacyFlow && _isDeleteAction"
              :key="`legacy-delete-${selectedTab}`"
              class="inline-inputs"
            >
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.emailLabel')"
                  :placeholder="$t('PeopleService.Modal.labels.emailLabel')"
                  :items="peopleColumns"
                  :rules="[rules.required]"
                  :selectedItem="_columnByAttribute.email || ''"
                  @selectOption="handleSelectOption($event, 'email')"
                />
              </div>
            </div>
            <!-- Selects do fluxo NOVO (branch): 15 attributes -->
            <div
              v-if="selectedTab && !_isLegacyFlow && _isAddUpdateAction"
              :key="`add-update-${selectedTab}`"
              class="inline-inputs"
            >
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.personID')"
                  :placeholder="$t('PeopleService.Modal.labels.personID')"
                  :items="peopleColumns"
                  :selectedItem="_columnByAttribute.personID || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'personID')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.peopleCodeLabel')"
                  :placeholder="
                    $t('PeopleService.Modal.labels.peopleCodeLabel')
                  "
                  :items="peopleColumns"
                  :selectedItem="_columnByAttribute.code || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'code')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.columnLabelFirstName')"
                  :placeholder="
                    $t('PeopleService.Modal.labels.columnLabelFirstName')
                  "
                  :items="peopleColumns"
                  :rules="[rules.required]"
                  :selectedItem="_columnByAttribute.firstname || ''"
                  @selectOption="handleSelectOption($event, 'firstname')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.columnLabelLastName')"
                  :placeholder="
                    $t('PeopleService.Modal.labels.columnLabelLastName')
                  "
                  :items="peopleColumns"
                  :rules="[rules.required]"
                  :selectedItem="_columnByAttribute.lastname || ''"
                  @selectOption="handleSelectOption($event, 'lastname')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.emailLabel')"
                  :placeholder="$t('PeopleService.Modal.labels.emailLabel')"
                  :items="peopleColumns"
                  :rules="[rules.required]"
                  :selectedItem="_columnByAttribute.email || ''"
                  @selectOption="handleSelectOption($event, 'email')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.phoneNumber')"
                  :placeholder="$t('PeopleService.Modal.labels.phoneNumber')"
                  :items="peopleColumns"
                  :selectedItem="_columnByAttribute.phoneNumber || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'phoneNumber')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.attributesLabel')"
                  :placeholder="
                    $t('PeopleService.Modal.labels.attributesLabel')
                  "
                  :items="peopleColumns"
                  :selectedItem="_columnByAttribute.attributes || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'attributes')"
                />
              </div>
              <!--
                'Grupos de Permissionamento' (PLURAL) — CSV de grupos do
                fluxo novo. Title distintivo do select legado (§5.1).
              -->
              <div
                class="input-holder"
                :title="$t('PeopleService.Modal.labels.permissionGroupsHint')"
              >
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.permissionGroups')"
                  :placeholder="
                    $t('PeopleService.Modal.labels.permissionGroups')
                  "
                  :items="peopleColumns"
                  :selectedItem="_columnByAttribute.permissionGroups || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'permissionGroups')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.language')"
                  :placeholder="$t('PeopleService.Modal.labels.language')"
                  :items="peopleColumns"
                  :selectedItem="_columnByAttribute.language || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'language')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.status')"
                  :placeholder="$t('PeopleService.Modal.labels.status')"
                  :items="peopleColumns"
                  :selectedItem="_columnByAttribute.status || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'status')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.sendInvite')"
                  :placeholder="$t('PeopleService.Modal.labels.sendInvite')"
                  :items="peopleColumns"
                  :selectedItem="_columnByAttribute.sendInvite || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'sendInvite')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.birthDate')"
                  :placeholder="$t('PeopleService.Modal.labels.birthDate')"
                  :items="peopleColumns"
                  :selectedItem="_columnByAttribute.birthDate || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'birthDate')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.admissionDate')"
                  :placeholder="$t('PeopleService.Modal.labels.admissionDate')"
                  :items="peopleColumns"
                  :selectedItem="_columnByAttribute.admissionDate || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'admissionDate')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.directManager')"
                  :placeholder="$t('PeopleService.Modal.labels.directManager')"
                  :items="peopleColumns"
                  :selectedItem="_columnByAttribute.directManager || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'directManager')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.occupation')"
                  :placeholder="$t('PeopleService.Modal.labels.occupation')"
                  :items="peopleColumns"
                  :selectedItem="_columnByAttribute.occupation || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'occupation')"
                />
              </div>
            </div>
            <div
              v-if="selectedTab && !_isLegacyFlow && _isDeleteAction"
              :key="`delete-${selectedTab}`"
              class="inline-inputs"
            >
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.emailLabel')"
                  :placeholder="$t('PeopleService.Modal.labels.emailLabel')"
                  :items="peopleColumns"
                  :rules="[rules.required]"
                  :selectedItem="_columnByAttribute.email || ''"
                  @selectOption="handleSelectOption($event, 'email')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="$t('PeopleService.Modal.labels.personID')"
                  :placeholder="$t('PeopleService.Modal.labels.personID')"
                  :items="peopleColumns"
                  :selectedItem="_columnByAttribute.personID || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'personID')"
                />
              </div>
            </div>
          </v-container>
        </v-form>

        <v-divider v-show="showForm && selectedTab"></v-divider>

        <div class="dialog-footer" v-show="showForm && selectedTab">
          <v-btn
            @click="handleDialog()"
            class="dialog-footer-btnCancel"
            depressed
            plain
          >
            {{ $t('PeopleService.modalButtons.cancel') }}
          </v-btn>

          <v-btn
            @click="SendTabsInformations"
            class="dialog-footer-btn"
            depressed
          >
            {{ _buttonLabel }}
          </v-btn>
        </div>
      </div>
    </template>
  </Modal>
</template>
<script>
import { PEOPLE_ACTIONS } from '@/helpers/upload-actions'
import {
  SYNC_FLOWS,
  DEFAULT_SYNC_FLAGS,
  PEOPLE_UPLOAD_OPTIONS,
  filterUploadOptionsByFlags,
  findUploadOption,
  resolveSyncFlags,
} from '@/helpers/upload-flows'
import {
  autoMatchColumns,
  PEOPLE_SHEET_TAB_NAMES,
  PEOPLE_COLUMN_CATALOG,
  PEOPLE_AUTO_MATCH_ATTRIBUTES,
  PEOPLE_LEGACY_COLUMN_CATALOG,
  PEOPLE_LEGACY_AUTO_MATCH_ATTRIBUTES,
} from '@/helpers/column-auto-match'
import { uploadTabSelectionMixin } from '@/mixins/upload-tab-selection'
import { calcTemplateUrlByLocale, LEGACY_TEMPLATE_URL } from '@/helpers/locale'
import AddFile from '@/views/SyncPage/Tabs/DataPage/Tabs/PeoplePage/Partials/UploadModal/Partials/AddFile/AddFile.vue'
import {
  verifySpreadSheet,
  sendTabsInformations,
} from '@/services/people/index'

export default {
  components: {
    AddFile,
  },
  mixins: [uploadTabSelectionMixin],
  data() {
    return {
      PEOPLE_ACTIONS,
      LEGACY_TEMPLATE_URL,
      // nomes esperados da aba do módulo (contrato do mixin de seleção)
      sheetTabCandidates: PEOPLE_SHEET_TAB_NAMES,
      // Flags por tenant (GET /sync/settings, D-H13). Default conservador
      // enquanto carrega/em erro: legado visível, "(Novo)" oculto.
      flags: { ...DEFAULT_SYNC_FLAGS },
      // Opção selecionada { action, flow } — default = 1ª opção legada,
      // como na develop (Criar e Atualizar)
      selectedOptionId: PEOPLE_UPLOAD_OPTIONS[0].id,
      action: PEOPLE_UPLOAD_OPTIONS[0].action,
      flow: PEOPLE_UPLOAD_OPTIONS[0].flow,
      showForm: false,
      fileName: '',
      loading: false,
      columns: {},
      tabsInformations: {
        fileLocationId: '',
        tab: {
          name: '',
          columns: [],
        },
      },
      selectedTab: '',
      valid: false,
      rules: {
        required: v => {
          const requiredLabel = this.$t('PeopleService.Modal.requiredField')

          if (v instanceof Array && v.length == 0) return requiredLabel
          return !!v || requiredLabel
        },
      },
    }
  },
  async mounted() {
    this.flags = await resolveSyncFlags()
    this.ensureAvailableOption()
  },
  watch: {
    selectedTab() {
      this.reset()
      this.applyAutoMatch()
    },
    // Único orquestrador da troca de opção (qualquer mudança de action E/OU
    // flow): limpa binds + aba e re-executa a auto-seleção da aba (regras
    // B2 no mixin), que re-dispara o auto-match válido para a NOVA opção.
    // Sem watches separados de action/flow — evitava-se rodar reset/
    // auto-match 2x quando os dois mudavam juntos.
    selectedOptionId(id) {
      const option = findUploadOption(PEOPLE_UPLOAD_OPTIONS, id)
      if (!option) return
      this.action = option.action
      this.flow = option.flow
      this.resetTabSelectionForOptionChange()
    },
  },
  computed: {
    _uploadOptions() {
      return filterUploadOptionsByFlags(PEOPLE_UPLOAD_OPTIONS, this.flags)
    },
    _isLegacyFlow() {
      return this.flow === SYNC_FLOWS.Legacy
    },
    _isAddUpdateAction() {
      return this.action === PEOPLE_ACTIONS.AddUpdate
    },
    _isDeleteAction() {
      return this.action === PEOPLE_ACTIONS.Delete
    },
    _v2TemplateUrl() {
      return calcTemplateUrlByLocale(this.$i18n.locale)
    },
    _v2DownloadLabel() {
      // Com os dois fluxos habilitados o link novo ganha o sufixo "(Novo)"
      // para desambiguar; sozinho, mantém o rótulo padrão
      return this.flags.legacyImportEnabled
        ? this.$t('PeopleService.Modal.downloadSheetsNew')
        : this.$t('PeopleService.Modal.downloadSheets')
    },
    _headerInstruction() {
      // Instruções por fluxo (plano §4): texto da develop para o legado
      return this._isLegacyFlow
        ? this.$t('PeopleService.Modal.headerInstructionLegacy')
        : this.$t('PeopleService.Modal.headerInstruction')
    },
    _fileInstruction() {
      return this._isLegacyFlow
        ? this.$t('PeopleService.Modal.fileInstructionLegacy')
        : this.$t('PeopleService.Modal.fileInstruction')
    },
    tabs() {
      return this.columns.tabs?.map(el => el.name)
    },
    peopleColumns() {
      return this.columns?.tabs?.find(el => el.name === this.selectedTab)
        ?.columns
    },
    // coluna mapeada por attribute — alimenta o selectedItem dos selects
    // (pré-preenchimento do auto-match e escolha manual ficam em sincronia)
    _columnByAttribute() {
      return this.tabsInformations.tab.columns.reduce(
        (map, { column, attribute }) => ({ ...map, [attribute]: column }),
        {}
      )
    },
    _buttonLabel() {
      return this.action === PEOPLE_ACTIONS.AddUpdate
        ? this.$t('PeopleService.modalButtons.loadPeople')
        : this.$t('PeopleService.modalButtons.deletePeople')
    },
  },
  methods: {
    initiliazeData() {
      this.selectOption(this._uploadOptions[0] || PEOPLE_UPLOAD_OPTIONS[0])
      this.showForm = false
      this.fileName = ''
      this.columns = {}
      this.selectedTab = ''
      this.tabsInformations = {
        fileLocationId: '',
        tab: {
          name: '',
          columns: [],
        },
      }
    },
    selectOption(option) {
      this.selectedOptionId = option.id
      this.action = option.action
      this.flow = option.flow
    },
    // Se as flags escondem a opção corrente (ex.: legado desligado),
    // seleciona a primeira disponível
    ensureAvailableOption() {
      const available = this._uploadOptions
      if (!available.length) return
      if (!findUploadOption(available, this.selectedOptionId)) {
        this.selectOption(available[0])
      }
    },
    reset() {
      this.tabsInformations.tab.columns = []
      // fix B4 — o v-dialog do Modal renderiza lazy: antes da primeira
      // abertura o form não existe (ex.: watcher disparado pelo
      // ensureAvailableOption no mounted em tenant v2-only)
      this.$refs.form?.reset()
    },
    handleDialog() {
      this.initiliazeData()
      this.$refs.modalPeopleUpload.handleDialog()
    },
    // Seleção/auto-seleção de aba: uploadTabSelectionMixin
    // (handleTabSelection, autoSelectTab, resetTabSelectionFor*)
    // Auto-identificação de colunas: pré-preenche o bind coluna→atributo
    // quando o header casa com um nome conhecido (match exato pós-
    // normalização). O catálogo é o do FLUXO selecionado (§3.3.3): opção
    // legada usa os headers do modelo antigo. Reusa handleSelectOption
    // (dedupe e shape garantidos) e NUNCA sobrescreve mapeamento existente.
    applyAutoMatch() {
      if (!this.selectedTab) return

      const catalog = this._isLegacyFlow
        ? PEOPLE_LEGACY_COLUMN_CATALOG
        : PEOPLE_COLUMN_CATALOG
      const allowedByAction = this._isLegacyFlow
        ? PEOPLE_LEGACY_AUTO_MATCH_ATTRIBUTES
        : PEOPLE_AUTO_MATCH_ATTRIBUTES

      const mappedAttributes = this.tabsInformations.tab.columns.map(
        el => el.attribute
      )

      autoMatchColumns(
        this.peopleColumns || [],
        catalog,
        allowedByAction[this.action] || []
      )
        .filter(({ attribute }) => !mappedAttributes.includes(attribute))
        .forEach(({ column, attribute }) =>
          this.handleSelectOption({ value: column }, attribute)
        )
    },
    handleSelectOption({ value }, name) {
      const remainingColumns = this.tabsInformations.tab.columns.filter(
        el => el.attribute !== name
      )

      // Limpeza (select clearable emite value vazio): remove o mapeamento —
      // attribute opcional volta a ficar FORA do payload (opt-out do
      // auto-match sem precisar trocar de aba/ação)
      if (value === null || value === undefined || value === '') {
        this.tabsInformations.tab.columns = remainingColumns
        return
      }

      this.tabsInformations.tab.columns = [
        ...remainingColumns,
        { column: value, attribute: name },
      ]
    },
    async validateFile(file) {
      // O guard vem ANTES do spinner: ligar loading e so depois descobrir que
      // nao ha arquivo deixava o modal preso no spinner para sempre (o bloco
      // de instrucoes fica oculto com `!showForm && !loading`, e nada mais
      // apagava a flag)
      if (!file) return

      this.loading = true
      this.fileName = file.name
      // flow da opção corrente no verify (fix P1 do review do híbrido):
      // tenant v2-only já parte com flow='v2' (ensureAvailableOption) e
      // passa no gate validateFlowEnabled; o service só anexa o campo
      // quando v2 (legacy segue byte-idêntico à develop). Trocar de radio
      // após o verify é seguro: o parse é flow-agnóstico e o gate roda de
      // novo no POST /import.
      await verifySpreadSheet(file, this.flow)
        .then(response => {
          this.loading = false
          this.showForm = true
          this.columns = response.data
          // Auto-seleção da aba pós-upload (limpa estado de upload
          // anterior antes de re-selecionar)
          this.resetTabSelectionForNewFile()
        })
        .catch(error => {
          this.loading = false
          this.fileName = ''
          this.showForm = false
          this.$emit('alert', {
            type: 'error',
            message: error?.response?.data?.message || this.$t('uploadError'),
          })
        })
    },
    async SendTabsInformations() {
      // O RETORNO de validate() e sincrono (Vuetify 2: VForm.validate()
      // devolve o resultado na hora). O v-model `valid` so e atualizado pelo
      // watcher de errorBag, que e um watcher do Vue e portanto assincrono —
      // no primeiro clique com um select obrigatorio vazio `valid` ainda era
      // `true` e o envio passava, para o backend recusar com
      // "PARAMS_NOT_SENT" depois do upload em vez de a tela marcar o campo.
      if (!this.$refs.form.validate()) {
        return
      }

      this.tabsInformations.action = this.action
      // flow no payload SÓ no fluxo novo (plano §2.1/aceite F4-ii): a opção
      // legada envia o payload byte-idêntico ao da develop — o Joi do
      // sync-backend aplica o default 'legacy' na escrita
      if (this.flow === SYNC_FLOWS.V2) {
        this.tabsInformations.flow = SYNC_FLOWS.V2
      } else {
        delete this.tabsInformations.flow
      }
      this.tabsInformations.fileLocationId = this.columns.fileLocationId

      this.loading = true
      this.showForm = false

      await sendTabsInformations(this.tabsInformations)
        .then(response => {
          this.$emit('alert', {
            type: 'success',
            message: response.data.message,
          })
          this.loading = false
          this.$emit('updatePeopleList')
          this.handleDialog()
        })
        .catch(error => {
          this.loading = false
          this.showForm = true
          // Falha sem resposta HTTP (rede caiu, CORS, timeout) nao tem
          // error.response: acessar .data.message ali lancava TypeError
          // DENTRO do catch — rejeicao nao tratada e NENHUM feedback na tela.
          // Mesmo fallback do validateFile.
          this.$emit('alert', {
            type: 'error',
            message: error?.response?.data?.message || this.$t('uploadError'),
          })
        })
    },
  },
}
</script>

<style src="./style.scss" lang="scss" scoped></style>
